import { Box, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { CardWithHeader } from '@/components/layout/CardWithHeader';
import { useFormContext } from '@/components/react-hook-form';
import { inKindTransferToggleCopy } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { formatPercent } from '@/utils/formatting/percent';
import { validateNumberRange } from '@/utils/validators/validateNumberRange';

import { calculateTotalValue } from '../../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.utils';
import { SelectedAssetDetails } from '../../EstateWaterfallHypotheticalTransfers/SelectedAssetDetails';
import {
  computeTransferAmount,
  getTransferDiscountHelpText,
} from '../../EstateWaterfallHypotheticalTransfers/transferDiscount.utils';
import { TransferFundingSource } from '../../EstateWaterfallHypotheticalTransfers/TransferFundingSource';
import {
  HypotheticalSaleLoanFormPaths,
  HypotheticalSaleLoanFormShape,
} from '../EstateWaterfallHypotheticalSaleLoanModal.types';
import { useSaleLoanFormContext } from './context/SaleLoanForm.context';
import { useAssetOptions } from './hooks/useAssetOptions';

export function SaleLoanAssetsForm() {
  const {
    sellerId,
    assetOptions,
    assetOptionDetails,
    totalSellerValue,
    loading,
  } = useAssetOptions();
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const { possibleSellerLenders } = useSaleLoanFormContext();
  const [
    proRataPattern,
    assetsPattern,
    fundingKind,
    selectedAssetClassAndBusinessValues,
    selectedAssetIds,
    kind,
    applyEntityTaxDiscount,
    entityTaxDiscountRate,
    proRataAmount,
    proRataPercent,
  ] = useWatch({
    control,
    name: [
      'proRataPattern',
      'assetsPattern',
      'fundingKind',
      'selectedAssetClassAndBusinessValues',
      'selectedAssetClassesAndBusinessIds',
      '_kind',
      'applyEntityTaxDiscount',
      'entityTaxDiscountRate',
      'proRataAmount',
      'proRataPercent',
    ],
  });

  // get the asset details for the selectedAssetIds
  const selectedAssetDetails = useMemo(() => {
    if (!selectedAssetIds || !assetOptionDetails) return [];
    return compact(
      selectedAssetIds.map((id) => {
        const details = assetOptionDetails[id];
        if (!details) return null;
        return {
          id,
          ...details,
        };
      })
    ).sort((a, b) => {
      return a.totalValue.comparedTo(b.totalValue);
    });
  }, [selectedAssetIds, assetOptionDetails]);

  // This is the total value of the selected assets in the asset-specific pattern
  const totalSelectedValue = useMemo(() => {
    return calculateTotalValue({
      selectedAssetIds,
      selectedAssetClassAndBusinessValues,
      assetOptionDetails,
      pattern: assetsPattern,
    });
  }, [
    selectedAssetIds,
    selectedAssetClassAndBusinessValues,
    assetOptionDetails,
    assetsPattern,
  ]);

  const currentSellerLender = useMemo(() => {
    return possibleSellerLenders.find((l) => l.value === sellerId);
  }, [possibleSellerLenders, sellerId]);

  const transferAmount = useMemo(
    () =>
      computeTransferAmount({
        fundingKind,
        proRataPattern,
        proRataAmount,
        proRataPercent,
        totalSourceValue: totalSellerValue,
        totalAssetsValue: totalSelectedValue,
      }),
    [
      fundingKind,
      proRataPattern,
      proRataAmount,
      proRataPercent,
      totalSellerValue,
      totalSelectedValue,
    ]
  );

  const discountRateHelpText = useMemo(
    () =>
      getTransferDiscountHelpText({
        transferAmount,
        discountRate: entityTaxDiscountRate,
      }),
    [entityTaxDiscountRate, transferAmount]
  );

  const displayKind = kind === 'sale' ? 'sale' : 'loan';
  return (
    <Stack spacing={2}>
      <CardWithHeader title={`Fund ${displayKind} using`}>
        <TransferFundingSource<HypotheticalSaleLoanFormShape>
          control={control}
          displayKind={displayKind}
          proRataPattern={proRataPattern}
          sourceId={sellerId}
          sourceValue={totalSellerValue}
          assetOptions={assetOptions}
          helpTextAssetClassesAndBusinesses="Select a seller to see available assets"
          loading={loading}
        />
      </CardWithHeader>

      <SelectedAssetDetails
        control={control}
        fundingKind={fundingKind}
        selectedAssetDetails={selectedAssetDetails.map((ad) => ({
          displayName: ad.displayName,
          totalValueForClass: ad.totalValue,
          hasOwnershipStake: !!ad.ownershipPercentage,
          ownershipPercentage: ad.ownershipPercentage,
          id: ad.id,
        }))}
        assetsPattern={assetsPattern}
        totalSelectedValue={totalSelectedValue}
      />
      <Card variant="filled" sx={{ p: 2, pb: applyEntityTaxDiscount ? 4 : 3 }}>
        <Stack spacing={1}>
          <FormAwareSwitch<HypotheticalSaleLoanFormShape>
            labelPosition="right"
            fieldName={
              'applyEntityTaxDiscount' as const satisfies HypotheticalSaleLoanFormPaths
            }
            label={
              currentSellerLender?.effectiveDiscountPercent?.greaterThan(0)
                ? 'Override existing discount'
                : 'Apply discount'
            }
            control={control}
            helpText={
              currentSellerLender?.effectiveDiscountPercent?.greaterThan(0)
                ? `Source entity has an effective ${formatPercent(currentSellerLender.effectiveDiscountPercent, 2)}% valuation discount`
                : undefined
            }
          />
        </Stack>
        {applyEntityTaxDiscount && (
          <Box sx={{ pl: 7, pt: 2 }}>
            <FormAwarePercentInput<HypotheticalSaleLoanFormShape>
              fieldName={
                'entityTaxDiscountRate' as const satisfies HypotheticalSaleLoanFormPaths
              }
              label="Discount rate"
              control={control}
              hideLabel
              isDecimalJSInput
              helpText={discountRateHelpText}
              maxValue={100}
              required
              decimalScale={2}
              validation={{
                minMax: validateNumberRange({
                  min: 0,
                  max: 100,
                  fieldName: 'Discount rate',
                }),
              }}
            />
          </Box>
        )}
      </Card>
      <Card variant="filled" sx={{ p: 2, pb: 4 }}>
        <FormAwareSwitch
          labelPosition="right"
          fieldName={
            'forceLiquidationOnTransfer' as const satisfies HypotheticalSaleLoanFormPaths
          }
          {...inKindTransferToggleCopy}
          control={control}
        />
      </Card>
    </Stack>
  );
}
